import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { AUTH_EXPIRED_EVENT } from "../services/api";

const TOKEN_KEY = "bb_token";
const DEVICE_ID_KEY = "basketbooster_device_id";
const STORE_ID_KEY = "bb_current_store_id";

type AuthState = {
  token: string | null;
  deviceId: string;
  setToken: (token: string | null) => void;
  currentStoreId: number | null;
  setCurrentStoreId: (id: number | null) => void;
};

const AuthContext = createContext<AuthState | null>(null);

function ensureDeviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

function readStoredStoreId(): number | null {
  const raw = sessionStorage.getItem(STORE_ID_KEY);
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setTokenState] = useState<string | null>(() => sessionStorage.getItem(TOKEN_KEY));
  const [deviceId] = useState(() => ensureDeviceId());
  const [currentStoreId, setCurrentStoreIdState] = useState<number | null>(() => readStoredStoreId());

  const setToken = useCallback((next: string | null) => {
    if (next === null) {
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(STORE_ID_KEY);
      setCurrentStoreIdState(null);
    } else {
      sessionStorage.setItem(TOKEN_KEY, next);
    }
    setTokenState(next);
  }, []);

  const setCurrentStoreId = useCallback((id: number | null) => {
    if (id === null) {
      sessionStorage.removeItem(STORE_ID_KEY);
    } else {
      sessionStorage.setItem(STORE_ID_KEY, String(id));
    }
    setCurrentStoreIdState(id);
  }, []);

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === TOKEN_KEY) setTokenState(e.newValue);
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  useEffect(() => {
    const handler = () => setToken(null);
    window.addEventListener(AUTH_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handler);
  }, [setToken]);

  const value = useMemo(
    () => ({ token, deviceId, setToken, currentStoreId, setCurrentStoreId }),
    [token, deviceId, setToken, currentStoreId, setCurrentStoreId],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
